/**
 * Pod-DDD misc-cluster helpers — pure predicates + intent builder.
 *
 * Closes 4 P2 Library UI bugs (codex #677 #679 #712 #678). The view body
 * (LibraryDocumentsView.tsx) imports the predicates below and uses them
 * to gate the new menu items + the AlertDialog confirmation envelope.
 *
 * Per project pattern (PR-B agent-new-shared, PR-E prompt-editor-shared,
 * P1 #1 r1-p1-fixes), the predicates live next to the unit test so the
 * gates can be exercised without dragging in React/JSDOM.
 */

import type { DocumentKind } from '@argusai/shared';
import type {
  LibraryDocumentsAdapter,
  NormalizedLibraryDoc,
} from './LibraryDocumentsView.types';

// ─── #712 — View / Download row item gates ────────────────────────────────

/**
 * `View` row item visibility predicate.
 *
 * - Articles: hidden. Clicking the row title already routes to the article
 *   reader, so the menu item would be a duplicate affordance.
 * - Files (uploaded) / synced: rendered when the adapter wired `onViewRow`.
 */
export function shouldRenderViewItem(
  adapter: LibraryDocumentsAdapter,
  doc: NormalizedLibraryDoc,
): boolean {
  if (!adapter.onViewRow) return false;
  return doc.kind === 'file' || doc.kind === 'synced';
}

/**
 * `Download` row item visibility predicate.
 *
 * - Articles: hidden — articles are HTML; the article reader exposes
 *   Export/Print separately (#689 tracks reader-side download UX).
 * - File (uploaded) and synced: rendered unless ingest failed (no usable
 *   file). A synced document is a document row like a file (decision 19);
 *   before it carried its own kind it was listed as an uploaded row and its
 *   failed ingest hid Download, and it still does (LB3T5-BF-03).
 */
export function shouldRenderDownloadItem(
  adapter: LibraryDocumentsAdapter,
  doc: NormalizedLibraryDoc,
): boolean {
  if (!adapter.onDownloadRow) return false;
  if (doc.kind === 'article') return false;
  if (hasFailedIngest(doc)) return false;
  return doc.kind === 'file' || doc.kind === 'synced';
}

/**
 * True for a file or synced document whose ingest failed — there is no
 * usable file behind it, so Download is hidden and Publish disabled
 * (LB3T5-BF-03: one predicate for both, for every kind that is ingested).
 */
export function hasFailedIngest(doc: NormalizedLibraryDoc): boolean {
  return (doc.kind === 'file' || doc.kind === 'synced') && doc.ingestStatus === 'failed';
}

// #678's disabled "Folder permissions — Articles use folder ACLs" row hint is
// DELETED (Library LB6-T3, LX-03): an article is a document (decision 19) and
// its row offers Share like any other row, for a person who holds
// share:library — the hint described a rule that no longer exists.

// ─── #677 / #679 — Delete confirmation intent envelope ───────────────────

/**
 * Trigger args for `buildDeleteIntent`.
 *
 * The view body owns two delete trigger paths:
 *   - row-level (#677): a single doc from the row dropdown
 *   - bulk-level (#679): any number of selected docs from the bulk bar
 *
 * Both flow through the same AlertDialog. The intent envelope tells the
 * dialog what title / body / counts to show + which ids to send to the
 * adapter on confirm.
 */
export type DeleteIntentTrigger =
  | { kind: 'row'; doc: NormalizedLibraryDoc }
  | { kind: 'bulk'; docs: NormalizedLibraryDoc[] };

export interface DeleteIntent {
  scope: 'row' | 'bulk';
  /** Only meaningful for row scope; bulk scope uses 'mixed' when the selection spans kinds. */
  docKind: DocumentKind | 'mixed';
  title: string;
  body: string;
  confirmLabel: string;
  cancelLabel: string;
  ids: string[];
  count: number;
}

/**
 * Build the AlertDialog payload for a row or bulk delete trigger.
 *
 * Returns a never-null `DeleteIntent` — caller is responsible for
 * gating the trigger on a non-empty selection (the view body short-
 * circuits when `selectedIds.size === 0`).
 */
/**
 * The ONE delete confirmation of an article, wherever it is deleted from —
 * the table's row menu, the sidebar's page menu and the editor (Library
 * LB6-T2, L6-18: "deleting an article from the sidebar and from the table
 * opens the SAME in-app dialog"). The row path below builds an article's
 * intent through this function.
 */
export function buildArticleDeleteIntent(article: { id: string; title: string }): DeleteIntent {
  return {
    scope: 'row',
    docKind: 'article',
    title: 'Delete article?',
    body: `"${article.title}" will be permanently removed from the wiki and the RAG index. This action cannot be undone.`,
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
    ids: [article.id],
    count: 1,
  };
}

export function buildDeleteIntent(trigger: DeleteIntentTrigger): DeleteIntent {
  if (trigger.kind === 'row') {
    const { doc } = trigger;
    if (doc.kind === 'article') return buildArticleDeleteIntent(doc);
    return {
      scope: 'row',
      docKind: doc.kind,
      title: 'Delete document?',
      body: `"${doc.title}" will be permanently removed, including its stored file and any embeddings. This action cannot be undone.`,
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      ids: [doc.id],
      count: 1,
    };
  }

  // Bulk path.
  const { docs } = trigger;
  const kinds = new Set(docs.map((d) => d.kind));
  const kind: DeleteIntent['docKind'] = kinds.size === 1
    ? (docs[0]?.kind ?? 'mixed')
    : 'mixed';
  const count = docs.length;
  const noun = `selected document${count === 1 ? '' : 's'}`;
  return {
    scope: 'bulk',
    docKind: kind,
    title: count === 1 ? 'Delete document?' : `Delete ${count} documents?`,
    body: `${count} ${noun} will be permanently removed, including their stored files and embeddings. This action cannot be undone.`,
    confirmLabel: 'Delete',
    cancelLabel: 'Cancel',
    ids: docs.map((d) => d.id),
    count,
  };
}
